import React, { useEffect, useState } from 'react';
import { WifiOff, RefreshCw, CloudUpload } from 'lucide-react';
import { toast } from 'sonner';
import { listQueue, syncQueue, discardQueued, QUEUE_EVENT } from '../utils/offlineStore';

export const usePendingSync = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    listQueue().then(setItems).catch(() => {});
    const onChange = (e) => setItems(e.detail || []);
    window.addEventListener(QUEUE_EVENT, onChange);
    return () => window.removeEventListener(QUEUE_EVENT, onChange);
  }, []);
  return items;
};

export const OfflineSyncChip = () => {
  const items = usePendingSync();
  const [online, setOnline] = useState(navigator.onLine);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  const syncNow = async () => {
    setBusy(true);
    const { synced, failed } = await syncQueue({ includeFailed: true });
    setBusy(false);
    if (synced) toast.success(`${synced} offline record${synced > 1 ? 's' : ''} synced`);
    if (failed) toast.error(`${failed} record${failed > 1 ? 's' : ''} couldn't sync. Tap the sync chip to review.`);
  };

  if (online && items.length === 0) return null;
  const failedCount = items.filter(i => i.error).length;
  return (
    <>
    {open && items.length > 0 && (
      <div data-testid="offline-queue-panel" className="fixed top-[112px] lg:top-12 left-1/2 -translate-x-1/2 z-[70] w-[92vw] max-w-sm bg-white rounded-xl shadow-xl border border-stone-200 p-3 space-y-2">
        <p className="text-sm font-semibold text-stone-800">Saved on this phone</p>
        {items.map(i => (
          <div key={i.id} data-testid={`offline-queue-item-${i.id}`} className={`rounded-lg border p-2 text-xs ${i.error ? 'border-red-200 bg-red-50' : 'border-stone-200'}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium text-stone-800">{i.label}{i.photos?.length ? ` · ${i.photos.length} photo${i.photos.length > 1 ? 's' : ''}` : ''}</span>
              <button data-testid={`offline-queue-discard-${i.id}`} type="button" onClick={() => { if (window.confirm('Discard this record? It will be deleted from this phone.')) discardQueued(i.id); }} className="text-red-600 hover:underline shrink-0">Discard</button>
            </div>
            <p className="text-stone-500">{new Date(i.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</p>
            {i.error && <p className="text-red-700 mt-1">{typeof i.error === 'string' ? i.error : 'Sync failed, please check this record'}</p>}
          </div>
        ))}
      </div>
    )}
    <div data-testid="offline-sync-chip" className="fixed top-[72px] lg:top-3 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 rounded-full shadow-lg px-3 py-1.5 text-xs font-medium bg-white border border-stone-200">
      {!online ? (
        <span className="flex items-center gap-1.5 text-red-600"><WifiOff className="h-3.5 w-3.5" /> Offline · saving on this phone</span>
      ) : (
        <span className="flex items-center gap-1.5 text-amber-700"><CloudUpload className="h-3.5 w-3.5" /> {items.length} waiting to sync{failedCount ? ` (${failedCount} need attention)` : ''}</span>
      )}
      {online && items.length > 0 && (
        <button data-testid="offline-sync-now" type="button" onClick={syncNow} disabled={busy} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white">
          <RefreshCw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} /> Sync now
        </button>
      )}
      {!online && items.length > 0 && <span className="text-stone-500">{items.length} pending</span>}
      {items.length > 0 && (
        <button data-testid="offline-queue-toggle" type="button" onClick={() => setOpen(o => !o)} className="text-stone-500 underline">{open ? 'Hide' : 'Review'}</button>
      )}
    </div>
    </>
  );
};
